// screens-showcase.tsx - Showcase (featured original + community grid) and developer profiles
// Card anatomy follows store conventions: cover, title, developer, tags.
// Exports: ScreenShowcase, ScreenDevProfile, ShowcaseCard, ShowcaseFeatured, SHOWCASE_GAMES
import { useEffect, useState } from 'react';
import { Link } from '../design/router';
import { CenteredLoading, CtaBand, MarketingFooter, MarketingNav, NotFoundScreen, PlayCta, SearchIcon } from '../components/ui-shared';
import { ImageSlot } from '../components/ImageSlot';
import { toast } from '../design/toast';
import { getPublicDevProfile, type PublicDevProfile } from '../services/devProfile';
import { listPublicGames, type PublicGame } from '../play/playClient';
import { keyArt, loadOriginalsMedia, mediaSrc, mergeMedia, type GameMedia } from './media';
import { TabStrip, type TabDescriptor } from '../../engine/editor/components/Tabs';

export interface ShowcaseGame {
  id: string;
  name: string;
  dev: string;
  devSlug: string | null;
  tags: string[];
  players?: string;
  slug: string;
  /** Set only when the game has a *public* wiki. */
  wikiUrl?: string | null;
  /** True when the api's dev.is_original flag is set (the publication's
   * tenant matches settings.originals_tenant_id) - drives the Originals/
   * Community tabs below, independent of studio_name/slug text. */
  isOriginal: boolean;
  /** Curated cover, screenshots, trailer, tagline and tags - the api's own
   * `media` merged with the bundled Originals manifest (see ./media.ts).
   * Null when nobody has curated any. */
  media: GameMedia | null;
}

// Sample games shown only when the API is unreachable, so the showcase never
// renders fully empty on a network hiccup. The real grid (ScreenShowcase)
// fetches GET /play and falls back to this only on fetch failure.
export const SHOWCASE_GAMES: ShowcaseGame[] = [];

/** Which original is shown in the featured hero slot - a curatorial choice
 * (see FEATURED_COPY below), not automatically the first Original found. */
const FEATURED_SLUG = 'sunder';

/** Fallback shown only while the api hasn't returned FEATURED_SLUG's row yet
 * (first paint) or the fetch fails outright - same "never render fully
 * empty" posture as SHOWCASE_GAMES above. Once a real row for FEATURED_SLUG
 * loads, ScreenShowcase replaces this with the live one. */
const FEATURED_FALLBACK: ShowcaseGame = {
  id: FEATURED_SLUG, name: 'Sunder', dev: 'Soulstack Studio', devSlug: 'soulstack',
  tags: [], slug: FEATURED_SLUG, wikiUrl: null, isOriginal: true, media: null,
};

/** Copy the api doesn't carry (tagline, tags, a marketing "About" page) for
 * whichever game FEATURED_SLUG names. Keyed by slug so featuring a
 * different original just needs an entry added here, not a rewrite of
 * ShowcaseFeatured itself - which otherwise renders only real api fields. */
const FEATURED_COPY: Record<string, { tagline: string; tags: string[]; aboutTo: string }> = {
  sunder: {
    tagline: 'Five-a-side tactical combat in collapsing arenas. Built by the platform team, in this studio.',
    tags: ['Tactical FPS', '5v5', 'Free'],
    aboutTo: '/sunder',
  },
};

function toShowcaseGame(g: PublicGame, media: GameMedia | null): ShowcaseGame {
  return {
    id: g.slug,
    name: g.name,
    dev: g.dev?.studio_name || 'a Soulstack creator',
    devSlug: g.dev?.slug ?? null,
    // Tags now come from curated media rather than being permanently empty,
    // which is also what makes the search box below able to match on genre.
    tags: media?.tags ?? [],
    slug: g.slug,
    wikiUrl: g.wiki?.url ?? null,
    isOriginal: g.dev?.is_original ?? false,
    media,
  };
}

export interface DevProfile {
  name: string;
  joined: string;
  site: string;
  bio: string;
  stats: [string, string][];
  games: string[];
}

export function ShowcaseCard({ g }: { g: ShowcaseGame }) {
  return (
    <div className="ss-card" style={{ padding: 10, display: 'flex', flexDirection: 'column', color: 'var(--ss-text)' }}>
      <Link to={'/showcase/' + g.slug} className="thumb-wrap" style={{ display: 'block' }}>
        <ImageSlot shape="rounded" radius={10} placeholder={'Cover: ' + g.name}
          src={mediaSrc(keyArt(g.media)?.url) ?? undefined}
          alt={keyArt(g.media)?.alt || g.name + ' key art'}
          style={{ width: '100%', height: 190, display: 'block', pointerEvents: 'none' }} />
      </Link>
      <div style={{ padding: '14px 10px 9px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
          <Link to={'/showcase/' + g.slug} style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--ss-text)' }}>{g.name}</Link>
          <div style={{ display: 'flex', gap: 6 }}>
            {g.tags.map((t) => (
              <span key={t} style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--ss-text-3)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>{t}</span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
          {g.devSlug ? (
            <Link to={'/dev/' + g.devSlug} style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>
              by <span style={{ color: 'var(--ss-accent)', fontWeight: 500 }}>{g.dev}</span>
            </Link>
          ) : (
            <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>by {g.dev}</span>
          )}
          {g.media?.trailer && (
            <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--ss-accent)' }}>Trailer</span>
          )}
          {!g.media?.trailer && g.wikiUrl && (
            <a href={g.wikiUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--ss-text-3)' }}>
              Wiki
            </a>
          )}
        </div>
        {g.media?.tagline && (
          <p style={{ color: 'var(--ss-text-3)', fontSize: 12.5, lineHeight: 1.5, margin: 0, textWrap: 'pretty' }}>
            {g.media.tagline}
          </p>
        )}
      </div>
    </div>
  );
}

/** Matches ShowcaseCard's own shape, same reasoning as the dashboard's
 * ProjectCardSkeleton (screens-app.tsx): without this, the fetch-in-flight
 * state and the real "no community games yet" empty state were visually
 * identical, so every page load briefly told a visiting player the showcase
 * was empty before the real grid (if any) popped in. */
function ShowcaseCardSkeleton() {
  return (
    <div className="ss-card" style={{ padding: 10, display: 'flex', flexDirection: 'column' }} aria-hidden="true">
      <div className="ss-skeleton" style={{ width: '100%', height: 190, borderRadius: 10 }} />
      <div style={{ padding: '14px 10px 9px', display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div className="ss-skeleton" style={{ width: '55%', height: 15.5 }} />
        <div className="ss-skeleton" style={{ width: '35%', height: 12.5 }} />
      </div>
    </div>
  );
}

export function ShowcaseFeatured({ game }: { game: ShowcaseGame }) {
  const copy = FEATURED_COPY[game.slug];
  // Curated media wins over the hardcoded FEATURED_COPY entry: that map
  // exists because the api carried no tagline or tags at all, and it needed
  // a code change to feature a different game. Media now supplies both, so
  // FEATURED_COPY is only the fallback (and the source of the About link,
  // which is a marketing route rather than anything the api knows about).
  const tagline = game.media?.tagline ?? copy?.tagline;
  const tags = game.media?.tags?.length ? game.media.tags : (copy?.tags ?? []);
  const art = keyArt(game.media);
  return (
    <div className="ss-card ss-grid-collapse" style={{ padding: 12, display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 0, overflow: 'hidden', borderColor: 'var(--ss-border)' }}>
      <Link to={'/showcase/' + game.slug} className="thumb-wrap" style={{ display: 'block' }}>
        <ImageSlot shape="rounded" radius={12} placeholder={game.name + ' key art'}
          src={mediaSrc(art?.url) ?? undefined}
          alt={art?.alt || game.name + ' key art'}
          style={{ width: '100%', height: 360, display: 'block', pointerEvents: 'none' }} />
      </Link>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16, padding: '28px 32px' }}>
        <span className="ss-eyebrow">Featured · Our game</span>
        <h2 className="ss-display" style={{ fontSize: 40, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{game.name}</h2>
        {tagline && (
          <p style={{ color: 'var(--ss-text-2)', fontSize: 15, lineHeight: 1.6, textWrap: 'pretty' }}>{tagline}</p>
        )}
        {!!tags.length && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {tags.map((t) => (
              <span key={t} style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--ss-text-3)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '2px 9px' }}>{t}</span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <PlayCta to={'/play/' + game.slug}>Play now</PlayCta>
          <Link className="ss-btn ss-btn--ghost" to={'/showcase/' + game.slug}>
            {game.media?.trailer ? 'Watch trailer' : 'Screenshots'}
          </Link>
          {copy?.aboutTo && <Link className="ss-btn ss-btn--ghost" to={copy.aboutTo}>About</Link>}
        </div>
        {game.devSlug ? (
          <Link to={'/dev/' + game.devSlug} style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>
            by <span style={{ color: 'var(--ss-accent)', fontWeight: 500 }}>{game.dev}</span>
          </Link>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>by {game.dev}</span>
        )}
      </div>
    </div>
  );
}

export function ScreenShowcase() {
  const filters: TabDescriptor[] = [
    { id: 'All', label: 'All' },
    { id: 'Multiplayer', label: 'Multiplayer' },
    { id: 'Solo', label: 'Solo' },
    { id: 'Originals', label: 'Originals' },
    { id: 'Community', label: 'Community' },
  ];
  const [active, setActive] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [games, setGames] = useState<ShowcaseGame[]>(SHOWCASE_GAMES);
  const [featured, setFeatured] = useState<ShowcaseGame>(FEATURED_FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listPublicGames(), loadOriginalsMedia()])
      .then(([rows, originals]) => {
        if (cancelled) return;
        const withMedia = (g: PublicGame) => toShowcaseGame(g, mergeMedia(g.media, originals[g.slug.toLowerCase()]));
        const featuredRow = rows.find((g) => g.slug === FEATURED_SLUG);
        // Only replace the fallback when the live data actually has this
        // slug - if it's ever unpublished, keep showing the last-known
        // featured card rather than an empty hero.
        if (featuredRow) setFeatured(withMedia(featuredRow));
        setGames(rows.filter((g) => g.slug !== FEATURED_SLUG).map(withMedia));
      })
      .catch((err: unknown) => {
        // Fail-soft: the featured hero still renders; the grid just stays
        // whatever it was (the sample fallback on first load) - but that
        // used to be entirely silent, indistinguishable from a quiet
        // community with no published games yet.
        console.error('[ScreenShowcase] failed to load public games:', err);
        toast('Could not load community games. Showing what we have.', 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredGames = games.filter((g) => {
    // Multiplayer/Solo have no backing field yet - only Originals/Community
    // actually filter, both on the same live isOriginal flag.
    if (active === 'Originals' && !g.isOriginal) return false;
    if (active === 'Community' && g.isOriginal) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      g.name.toLowerCase().includes(q) ||
      g.dev.toLowerCase().includes(q) ||
      g.tags.some((t) => t.toLowerCase().includes(q))
    );
  });

  return (
    <div className="ss-screen">
      <MarketingNav active="Showcase" />
      <header style={{ padding: '72px 28px 36px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <span className="ss-eyebrow">Showcase</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 8.5vw, 3.25rem)' }}>Made with Soulstack</h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 16.5, maxWidth: 540 }}>
            Every game here runs in a browser tab, hosted on the platform it was built on.
          </p>
        </div>
      </header>
      <section style={{ padding: '0 28px 110px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <ShowcaseFeatured game={featured} />
          {loading ? (
            <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, marginTop: 14 }}>
              <ShowcaseCardSkeleton />
              <ShowcaseCardSkeleton />
              <ShowcaseCardSkeleton />
            </div>
          ) : games.length === 0 ? (
            <div style={{ padding: '44px 0 8px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 15.5 }}>
                Community games will appear here as creators publish.
              </p>
              <p style={{ color: 'var(--ss-text-3)', fontSize: 13.5 }}>
                Soulstack Studio is in early access - Sunder is live now.
              </p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 14 }}>
                <TabStrip listLabel="Filter games" variant="compact" tabs={filters} activeId={active} onSelect={setActive} />
                <div className="ss-search" style={{ width: 250 }}>
                  <SearchIcon />
                  <input
                    className="ss-input"
                    placeholder="Search games"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '9px 14px 9px 35px', fontSize: 13.5 }}
                  />
                </div>
              </div>
              {filteredGames.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'var(--ss-text-3)', fontSize: 14 }}>No games match "{searchQuery}".</span>
                  <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => setSearchQuery('')}>Clear search</button>
                </div>
              ) : (
                <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
                  {filteredGames.map((g) => <ShowcaseCard key={g.id} g={g} />)}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <CtaBand />
      <MarketingFooter />
    </div>
  );
}

/* ── Developer profile (public page; edited from Account → Public profile) ── */
function DevProfileNotFound() {
  return (
    <div className="ss-screen">
      <MarketingNav active="Showcase" />
      <NotFoundScreen
        style={{ minHeight: '60vh' }}
        title="No studio here."
        body="This link doesn't match a public developer profile. It may be mistyped, or the studio may not have published one yet."
        ctas={[{ label: 'Browse the showcase', to: '/showcase' }]}
      />
      <MarketingFooter />
    </div>
  );
}

export function ScreenDevProfile({ slug = 'soulstack' }: { slug?: string }) {
  // Every slug, including 'soulstack', is treated uniformly now that a real
  // dev_profiles row exists for the platform's own tenant - an unknown,
  // mistyped, or genuinely failed slug gets an honest not-found page rather
  // than silently borrowing another studio's identity for the length of a
  // fetch or a transient error.
  const [real, setReal] = useState<PublicDevProfile | null>(null);
  const [originals, setOriginals] = useState<Record<string, GameMedia>>({});
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setReal(null);
    setNotFound(false);
    setLoading(true);
    // The developer profile lists the same ShowcaseCards as the grid, so it
    // needs the same media to render covers rather than placeholders.
    void loadOriginalsMedia().then((m) => { if (!cancelled) setOriginals(m); });
    getPublicDevProfile(slug)
      .then((p) => {
        if (!cancelled) setReal(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[ScreenDevProfile] failed to load dev profile:', err);
        setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) return <DevProfileNotFound />;
  if (loading || !real) {
    return (
      <div className="ss-screen">
        <MarketingNav active="Showcase" />
        <CenteredLoading text="Loading studio profile…" />
        <MarketingFooter />
      </div>
    );
  }

  const joinedYear = real.joined ? String(new Date(real.joined).getFullYear()) : null;
  const d: DevProfile = {
    name: real.studio_name || real.slug,
    joined: joinedYear ? `Building since ${joinedYear}` : 'Building on Soulstack',
    site: real.website,
    bio: real.bio,
    stats: [
      [String(real.games.length), real.games.length === 1 ? 'Game' : 'Games'],
      [joinedYear ?? 'New', 'Since'],
      ['Soulstack', 'Built on'],
    ],
    games: real.games.map((g) => g.name),
  };
  const games: ShowcaseGame[] = real.games.map((g) => {
    const media = mergeMedia(null, originals[g.slug.toLowerCase()]);
    return {
      id: g.slug, name: g.name, dev: d.name, devSlug: real.slug, tags: media?.tags ?? [], slug: g.slug,
      wikiUrl: g.wiki?.url ?? null, isOriginal: true, media,
    };
  });
  return (
    <div className="ss-screen">
      <MarketingNav active="Showcase" />
      {/* banner */}
      <div style={{ position: 'relative', height: 240 }}>
        <ImageSlot shape="rect" placeholder="Studio banner"
          style={{ width: '100%', height: '100%', display: 'block' }} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(6,6,7,0.2), rgba(6,6,7,0.9))' }} />
      </div>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 28px 90px' }}>
        {/* identity row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, marginTop: -44, position: 'relative', marginBottom: 30 }}>
          <ImageSlot shape="rounded" radius={20} placeholder="Avatar"
            style={{ width: 104, height: 104, display: 'block', flexShrink: 0, border: '1px solid var(--ss-border)', background: 'var(--ss-bg-2)' }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 4 }}>
            <h1 className="ss-display" style={{ fontSize: 'clamp(1.5rem, 6vw, 2.125rem)' }}>{d.name}</h1>
            <div style={{ display: 'flex', gap: 18, fontSize: 12.5, color: 'var(--ss-text-3)' }}>
              <span>{d.joined}</span>
              {d.site && (
                <a
                  href={d.site.startsWith('http') ? d.site : 'https://' + d.site}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--ss-accent)' }}
                >
                  {d.site}
                </a>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, paddingBottom: 8 }}>
            <button className="ss-btn ss-btn--primary ss-btn--sm">Follow</button>
            <button className="ss-btn ss-btn--ghost ss-btn--sm">Share</button>
          </div>
        </div>
        {/* bio + stats */}
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 40, marginBottom: 40 }}>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 15.5, lineHeight: 1.7, maxWidth: 560, textWrap: 'pretty' }}>{d.bio}</p>
          <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {d.stats.map(([n, l]) => (
              <div key={l} className="stat-card" style={{ padding: '14px 16px' }}>
                <span className="n" style={{ fontSize: 22 }}>{n}</span>
                <span className="l">{l}</span>
              </div>
            ))}
          </div>
        </div>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 16 }}>Games</h2>
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {games.map((g) => <ShowcaseCard key={g.id} g={{ ...g, id: 'dev-' + g.id }} />)}
        </div>
        <p style={{ marginTop: 32, fontSize: 12.5, color: 'var(--ss-text-3)' }}>
          Your page? Edit it under Account settings, Public profile.
        </p>
      </div>
      <MarketingFooter />
    </div>
  );
}
